/**
 * web/src/components/settings/GmailConnectionCard.tsx
 *
 * Phase 6.5 UI-14, multi-account rewrite (2026-06). Reads from useMe() and
 * renders:
 *
 *   - loading (useMe.isPending)        → <Skeleton variant="card" />
 *   - NO connections                   → Connect Gmail primary CTA
 *   - ≥1 connection                    → per-connection rows:
 *       healthy    → email + connected-on date + Connected check + Disconnect
 *       tombstoned → email + "Reconnect required" + Reconnect button
 *     plus a "Connect another account" button, and — when the user has 2+
 *     connections — a per-event "Default sending account" picker (one native
 *     Select per active event; blank = first-connected fallback).
 *
 * Connect / Reconnect actions are FULL-PAGE NAVIGATION
 * (window.location.assign('/api/auth/gmail/login')) — never a modal or popup
 * per UI-SPEC § Anti-patterns. Google's account chooser decides WHICH mailbox
 * gets connected; the server records whichever account was actually
 * authorized. Disconnect uses the Radix <Dialog> wrapper from
 * web/src/components/ui/dialog.tsx — never window.confirm.
 *
 * Tokens: only theme tokens (bg-completed, text-failed, bg-failed-soft,
 * border-border, text-text-muted, etc.) — zero raw palette utilities, zero
 * hex in JSX (UI-12).
 */
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useMe } from '../../hooks/queries/useMe';
import { useEvents } from '../../hooks/queries/useEvents';
import { useDisconnectGmail } from '../../hooks/mutations/useDisconnectGmail';
import { useSetGmailEventDefault } from '../../hooks/mutations/useSetGmailEventDefault';
import type { GmailConnectionInfo } from '../../api/types';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Select } from '../shared/Select';
import { Skeleton } from '../shared/Skeleton';
import { Icon } from '../../lib/icons';
import { gmailReconnectMessage } from '../../lib/gmailErrorMessage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';

/**
 * Navigates the browser to the OAuth login route. Wrapped so tests can
 * assert via a single button click without stubbing window.location.
 */
function startConnect(): void {
  window.location.assign('/api/auth/gmail/login');
}

/**
 * Formats an ISO date for the "Connected on ..." sub-line.
 * Falls back to an empty string when the input is malformed so the card
 * never crashes on a malformed wire payload.
 */
function formatConnectedAt(iso: string | null): string {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return '';
  }
}

/** A connection that can mint tokens right now (non-null reason ⇔ tombstoned). */
function isHealthy(c: GmailConnectionInfo): boolean {
  return c.lastErrorReason == null;
}

/**
 * Renders the Gmail connections card. Consumes the single ['me'] query as the
 * source of truth — there is NO parallel useGmailConnection() hook
 * (UI-SPEC § Data Wiring Contract).
 *
 * @sideEffects subscribes to useMe + useEvents; triggers full-page navigation
 *              on Connect/Reconnect; opens a Radix Dialog on Disconnect;
 *              PUTs per-event defaults on Select change.
 * @idempotent yes — re-renders against the same ['me'] cache entry.
 */
export function GmailConnectionCard() {
  const { data: me, isPending } = useMe();
  const disconnect = useDisconnectGmail();
  // Pending-disconnect target; null = dialog closed.
  const [confirmTarget, setConfirmTarget] = useState<GmailConnectionInfo | null>(null);

  // Loading: skeleton card body. Card-level skeleton, NOT a skeleton on the
  // title — matches UI-SPEC § "Loading state copy".
  if (isPending || !me) {
    return (
      <Card>
        <Card.Head>
          <Icon name="mail" size={16} />
          <Card.Title>Gmail</Card.Title>
        </Card.Head>
        <Card.Body>
          <Skeleton variant="card" />
        </Card.Body>
      </Card>
    );
  }

  const connections = me.gmailConnections ?? [];
  const healthy = connections.filter(isHealthy);

  // NO CONNECTIONS — primary "Connect Gmail" CTA. Full-page nav, not a modal.
  if (connections.length === 0) {
    return (
      <Card>
        <Card.Head>
          <Icon name="mail" size={16} />
          <Card.Title>Gmail</Card.Title>
        </Card.Head>
        <Card.Body className="flex flex-col gap-4">
          <p className="text-[13px] text-text-muted">
            Required so workflow approval emails send from your Gmail. We only request &ldquo;Send
            email&rdquo; — never read your inbox.
          </p>
          <Button
            variant="primary"
            size="default"
            className="mt-1 self-start"
            onClick={startConnect}
          >
            <Icon name="mail" size={14} />
            Connect Gmail
          </Button>
        </Card.Body>
      </Card>
    );
  }

  // ≥1 CONNECTION — per-connection rows + Connect-another + event defaults.
  return (
    <>
      <Card>
        <Card.Head>
          <Icon name="mail" size={16} />
          <Card.Title>Gmail</Card.Title>
          {healthy.length > 0 ? (
            <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-completed font-medium">
              <Icon name="check" size={14} />
              {healthy.length === connections.length
                ? 'Connected'
                : `${healthy.length} of ${connections.length} connected`}
            </span>
          ) : (
            <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-failed font-medium">
              <Icon name="alert" size={14} />
              Reconnect required
            </span>
          )}
        </Card.Head>
        <Card.Body className="flex flex-col gap-0 p-0">
          <ul className="divide-y divide-border">
            {connections.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] text-text truncate">{c.email}</p>
                  {isHealthy(c) ? (
                    <>
                      {formatConnectedAt(c.connectedAt) && (
                        <p className="text-[12px] text-text-subtle tabular mt-0.5">
                          Connected on {formatConnectedAt(c.connectedAt)}
                        </p>
                      )}
                      {/* Grant predates gmail.settings.basic → sends omit the
                          signature until the user re-consents. */}
                      {!c.canReadSignature && (
                        <p className="text-[12px] text-warn mt-0.5">
                          Reconnect to append your Gmail signature to the emails Pixel sends.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-[12px] text-failed mt-0.5">
                      {gmailReconnectMessage(c.lastErrorReason)}
                    </p>
                  )}
                </div>
                {(!isHealthy(c) || !c.canReadSignature) && (
                  <Button variant="primary" size="sm" onClick={startConnect}>
                    Reconnect
                  </Button>
                )}
                <Button variant="default" size="sm" onClick={() => setConfirmTarget(c)}>
                  Disconnect
                </Button>
              </li>
            ))}
          </ul>
          <div className="px-5 py-3 border-t border-border">
            <Button variant="default" size="sm" onClick={startConnect}>
              <Icon name="mail" size={14} />
              Connect another account
            </Button>
          </div>
          {connections.length > 1 && <EventDefaultsSection connections={connections} />}
        </Card.Body>
      </Card>

      {/* Destructive confirm — Radix Dialog (not native confirm; UI-SPEC anti-pattern) */}
      <Dialog
        open={confirmTarget != null}
        onOpenChange={(open) => {
          if (!open) setConfirmTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect {confirmTarget?.email ?? 'Gmail'}?</DialogTitle>
            <DialogDescription>
              {healthy.filter((c) => c.id !== confirmTarget?.id).length > 0
                ? 'Workflow approval emails will send from your remaining connected account(s). Any event defaults pointing at this account are cleared.'
                : 'This is your only working connection — you won’t be able to send workflow approval emails until you reconnect.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {/* Cancel rendered FIRST so Radix focus management lands on the
                safer choice on open (UI-SPEC § Accessibility Contract). */}
            <Button
              variant="default"
              size="sm"
              onClick={() => setConfirmTarget(null)}
              disabled={disconnect.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={disconnect.isPending}
              onClick={() => {
                if (!confirmTarget) return;
                disconnect.mutate(confirmTarget.id, {
                  onSettled: () => setConfirmTarget(null),
                });
              }}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Yes, disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Per-event "Default sending account" pickers. One native Select per ACTIVE
 * event; blank option = no mapping (sends fall back to the first-connected
 * account; an approver can still override per-send at the gate). Only
 * rendered when the user has 2+ connections — with one account there is
 * nothing to choose.
 *
 * @param connections the user's full connection list (tombstoned rows are
 *        shown disabled so an existing mapping stays visible instead of
 *        silently vanishing)
 * @sideEffects subscribes to useEvents + useMe; PUTs on Select change.
 * @idempotent yes
 */
function EventDefaultsSection({ connections }: { connections: GmailConnectionInfo[] }) {
  const { data: me } = useMe();
  const { data: events, isPending } = useEvents();
  const setDefault = useSetGmailEventDefault();

  const activeEvents = (events ?? []).filter((e) => e.isActive);
  const defaults = me?.gmailEventDefaults ?? {};

  return (
    <div className="px-5 py-4 border-t border-border flex flex-col gap-3">
      <div>
        <p className="text-[13px] font-medium text-text">Default sending account per event</p>
        <p className="text-[12px] text-text-muted mt-0.5">
          Leave blank to send from your first-connected account. You can still pick a different
          account on each email at gate review.
        </p>
      </div>
      {isPending ? (
        <Skeleton variant="row" height={32} />
      ) : activeEvents.length === 0 ? (
        <p className="text-[12px] text-text-subtle">No active events.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {activeEvents.map((event) => (
            <li
              key={event.id}
              className="grid grid-cols-[1fr_auto] items-center gap-3 sm:grid-cols-1"
            >
              <label
                htmlFor={`gmail-default-${event.id}`}
                className="text-[13px] text-text truncate"
              >
                {event.name}
              </label>
              <Select
                id={`gmail-default-${event.id}`}
                value={defaults[event.id] ?? ''}
                disabled={setDefault.isPending}
                onChange={(e) =>
                  setDefault.mutate({
                    eventId: event.id,
                    connectionId: e.target.value || null,
                  })
                }
                className="min-w-[220px]"
              >
                <option value="">First connected (default)</option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id} disabled={c.lastErrorReason != null}>
                    {c.email}
                    {c.lastErrorReason != null ? ' (needs reconnect)' : ''}
                  </option>
                ))}
              </Select>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
